import { useMutation, useQueries, useQuery } from "react-query";
import {
  activateMatrix,
  ActivateMatrixRequest,
  archiveMatrix,
  downloadFactorTemplate,
  evaluateMatrix,
  exportMatrix,
  getAllMatrix,
  getMatrixById,
  getPlatformFields,
  saveDraft,
  SaveDraftRequest,
  updateMatrix,
  UpdateMatrixRequest,
  uploadCustomFactor,
} from "../api/allocationMatrix";
import {
  AllocationFactor,
  AllocationMode,
  NoMatchBehaviour,
  QuantumMode,
  RuleScope,
} from "../store/useAllocationMatrixStore";
import { getAllGrants } from "../api/Esop";
import { getTeamMemberGrants } from "../api/managerPortal";
import { Grant } from "../types/Grant";
import { queryClient } from "./client";

export type PlatformField = AllocationFactor & {
  category?: string;
  isPreviouslyUsed?: boolean;
};

export interface EvaluatedRule {
  priority: number;
  scope: RuleScope;
  allocationMode: AllocationMode;
  quantumMode: QuantumMode;
  quantumMin: number | null;
  quantumRecommended: number | null;
  quantumMax: number | null;
  costOrGain: import("../store/useAllocationMatrixStore").CostOrGain | null;
  conditions: Record<string, string[]>;
}

export interface EvaluationUtilization {
  scope?: RuleScope;
  totalPool?: number;
  allocated?: number;
  available?: number;
  vested?: number;
  proposed?: number;
  grantsThisYear?: number;
}

export interface AllocationEvaluation {
  eligible?: boolean;
  matched?: boolean;
  rule?: EvaluatedRule;
  matchedRule?: EvaluatedRule;
  matchedConditions?: { label: string; value: string }[];
  matchedOn?: Record<string, string | string[]>;
  noMatchBehaviour?: NoMatchBehaviour;
  message?: string;
  boundMin?: number;
  boundMax?: number;
  alreadyAllocated?: number;
  utilization?: EvaluationUtilization;
}

export function useGetPlatformFields() {
  return useQuery({
    queryKey: "allocationMatrixPlatformFields",
    queryFn: async () => {
      const response = await getPlatformFields();
      return (response.data?.data ?? []) as PlatformField[];
    },
  });
}

export function useGetAllMatrices() {
  return useQuery({
    queryKey: "allocationMatrices",
    queryFn: async () => {
      const response = await getAllMatrix();
      return (response.data?.data ?? []) as any[];
    },
  });
}

export function useGetMatrixById(matrixId: string) {
  return useQuery({
    queryKey: ["allocationMatrix", matrixId],
    queryFn: async () => {
      const response = await getMatrixById(matrixId);
      return response.data?.data;
    },
    enabled: !!matrixId,
  });
}

export function useSaveDraft() {
  return useMutation((data: SaveDraftRequest) => saveDraft(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("allocationMatrices");
    },
  });
}

export function useActivateMatrix() {
  return useMutation((data: ActivateMatrixRequest) => activateMatrix(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("allocationMatrices");
    },
  });
}

export function useUpdateMatrix() {
  return useMutation(
    ({ matrixId, data }: { matrixId: string; data: UpdateMatrixRequest }) =>
      updateMatrix(matrixId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("allocationMatrices");
      },
    }
  );
}

export function useArchiveMatrix() {
  return useMutation((matrixId: string) => archiveMatrix(matrixId), {
    onSuccess: () => {
      queryClient.invalidateQueries("allocationMatrices");
    },
  });
}

export function useEvaluateMatrix() {
  return useMutation(
    ({ matrixId, employeeId }: { matrixId: string; employeeId: string }) =>
      evaluateMatrix(matrixId, employeeId)
  );
}

export function useEvaluateMatrixQuery(
  matrixId?: string,
  employeeId?: string,
  grantId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["allocationMatrixEvaluation", matrixId, employeeId, grantId],
    queryFn: async () => {
      const response = await evaluateMatrix(matrixId!, employeeId!, grantId);
      return (response.data?.data ?? null) as AllocationEvaluation | null;
    },
    enabled: enabled && !!matrixId && !!employeeId,
  });
}

export function useDownloadFactorTemplate() {
  return useMutation((factorName: string) =>
    downloadFactorTemplate(factorName)
  );
}

export function useUploadCustomFactor() {
  return useMutation(
    ({ factorName, file }: { factorName: string; file: File }) =>
      uploadCustomFactor(factorName, file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("allocationMatrixPlatformFields");
      },
    }
  );
}

export function useExportMatrix() {
  return useMutation((matrixId: string) => exportMatrix(matrixId));
}

export function useEmployeeGrants(
  employeeId?: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ["employeeGrants", employeeId],
    queryFn: async () => {
      const response = await getAllGrants({ employeeId });
      return (response?.data?.grants ?? []) as Grant[];
    },
    enabled: !!employeeId && enabled,
  });
}

export function useMultipleEmployeeGrants(
  employeeIds: string[],
  managerScoped = false
) {
  return useQueries(
    employeeIds.map((empId) => ({
      queryKey: [
        managerScoped ? "managerEmployeeGrants" : "employeeGrants",
        empId,
      ],
      queryFn: async () => {
        if (managerScoped) {
          return (await getTeamMemberGrants(empId)) as Grant[];
        }
        const response = await getAllGrants({ employeeId: empId });
        return (response?.data?.grants ?? []) as Grant[];
      },
      enabled: !!empId,
    }))
  );
}
